import React, { useEffect, useRef, useState, useCallback } from "react";
import { Plus, Minus, Maximize2, RotateCcw } from "lucide-react";

/**
 * WebGraph — custom canvas force-directed graph for The Web.
 *
 * Why custom over a library: we need bespoke visual encoding (border-color
 * by funnel stage, halo by ROAS tier, pulse on scaling, ghost on
 * unexplored, edge type by relationship class) and full control over
 * hit-testing for hover/click — implementing Verlet + spring forces here
 * is ~150 LoC and avoids dragging another dep in.
 *
 * Props:
 *   nodes, edges  — the graph (filtered by parent)
 *   highlightId   — node to glow (e.g. from a daily card)
 *   selectedId    — node currently in detail panel
 *   onSelect      — (id) => void
 *   showHeatAtlas — overlay tested/untested heat (boolean)
 *   showGaps      — emphasize unexplored nodes (boolean)
 */
export default function WebGraph({
  nodes,
  edges,
  highlightId,
  selectedId,
  onSelect,
  showHeatAtlas,
  showGaps,
  dimmedIds,
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    parts: new Map(),       // id -> {x, y, vx, vy, r, mass}
    dragging: null,
    pan: { x: 0, y: 0 },
    zoom: 1,
    width: 800,
    height: 600,
    raf: null,
    pulsePhase: 0,
    hoverId: null,
  });
  const [hoverNode, setHoverNode] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  // --- Initialize / sync particle state when nodes change -------------
  useEffect(() => {
    const s = stateRef.current;
    const next = new Map();
    const cx = s.width / 2;
    const cy = s.height / 2;

    nodes.forEach((n, i) => {
      const existing = s.parts.get(n.id);
      const r = nodeRadius(n);
      if (existing) {
        next.set(n.id, { ...existing, r, mass: r * r * 0.02 });
      } else {
        const angle = (i / Math.max(1, nodes.length)) * Math.PI * 2;
        const dist = 220 + Math.random() * 120;
        next.set(n.id, {
          x: cx + Math.cos(angle) * dist + (Math.random() - 0.5) * 40,
          y: cy + Math.sin(angle) * dist + (Math.random() - 0.5) * 40,
          vx: 0, vy: 0,
          r,
          mass: r * r * 0.02,
        });
      }
    });

    s.parts = next;
  }, [nodes]);

  // --- Resize observer ------------------------------------------------
  useEffect(() => {
    const el = canvasRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => sizeCanvas());
    ro.observe(el);
    sizeCanvas();
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sizeCanvas = () => {
    const c = canvasRef.current;
    if (!c || !c.parentElement) return;
    const rect = c.parentElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    c.style.width = `${rect.width}px`;
    c.style.height = `${rect.height}px`;
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    stateRef.current.width = rect.width;
    stateRef.current.height = rect.height;
  };

  // --- Animation loop -------------------------------------------------
  useEffect(() => {
    const tick = () => {
      step(stateRef.current, nodes, edges);
      draw(canvasRef.current, stateRef.current, nodes, edges, {
        highlightId,
        selectedId,
        showHeatAtlas,
        showGaps,
        dimmedIds,
      });
      stateRef.current.raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(stateRef.current.raf);
  }, [nodes, edges, highlightId, selectedId, showHeatAtlas, showGaps, dimmedIds]);

  // --- Pointer interaction --------------------------------------------
  const screenToWorld = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const s = stateRef.current;
    return {
      sx, sy,
      x: (sx - s.pan.x) / s.zoom,
      y: (sy - s.pan.y) / s.zoom,
    };
  };

  const findNodeAt = (wx, wy) => {
    const s = stateRef.current;
    for (const n of nodes) {
      const p = s.parts.get(n.id);
      if (!p) continue;
      const dx = wx - p.x;
      const dy = wy - p.y;
      if (dx * dx + dy * dy < (p.r + 4) ** 2) return n;
    }
    return null;
  };

  const handleMove = useCallback((e) => {
    const { x, y, sx, sy } = screenToWorld(e);
    const s = stateRef.current;
    if (s.dragging === "pan") {
      s.pan.x += e.movementX;
      s.pan.y += e.movementY;
      return;
    }
    if (s.dragging?.id) {
      const p = s.parts.get(s.dragging.id);
      if (p) {
        p.x = x;
        p.y = y;
        p.vx = 0; p.vy = 0;
      }
      return;
    }
    const node = findNodeAt(x, y);
    s.hoverId = node?.id || null;
    if (node) {
      setHoverNode(node);
      setHoverPos({ x: sx, y: sy });
    } else if (hoverNode) {
      setHoverNode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, hoverNode]);

  const handleDown = (e) => {
    const { x, y } = screenToWorld(e);
    const node = findNodeAt(x, y);
    const s = stateRef.current;
    if (node) {
      s.dragging = { id: node.id };
    } else {
      s.dragging = "pan";
    }
  };

  const handleUp = (e) => {
    const s = stateRef.current;
    const { x, y } = screenToWorld(e);
    const node = findNodeAt(x, y);
    if (s.dragging?.id && node?.id === s.dragging.id) {
      onSelect?.(node.id);
    } else if (s.dragging === "pan" && !node) {
      // click on empty area — clear selection
      onSelect?.(null);
    }
    s.dragging = null;
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const s = stateRef.current;
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const wx = (mx - s.pan.x) / s.zoom;
    const wy = (my - s.pan.y) / s.zoom;
    s.zoom = Math.max(0.3, Math.min(3, s.zoom * factor));
    s.pan.x = mx - wx * s.zoom;
    s.pan.y = my - wy * s.zoom;
  };

  const zoomBy = (factor) => {
    const s = stateRef.current;
    const cx = s.width / 2;
    const cy = s.height / 2;
    const wx = (cx - s.pan.x) / s.zoom;
    const wy = (cy - s.pan.y) / s.zoom;
    s.zoom = Math.max(0.3, Math.min(3, s.zoom * factor));
    s.pan.x = cx - wx * s.zoom;
    s.pan.y = cy - wy * s.zoom;
  };

  const recenter = () => {
    const s = stateRef.current;
    s.pan = { x: 0, y: 0 };
    s.zoom = 1;
    // also gently nudge particles back toward center
    s.parts.forEach((p) => {
      p.vx = 0; p.vy = 0;
    });
  };

  const reheat = () => {
    const s = stateRef.current;
    s.parts.forEach((p) => {
      p.vx += (Math.random() - 0.5) * 8;
      p.vy += (Math.random() - 0.5) * 8;
    });
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        className="web-canvas"
        onPointerMove={handleMove}
        onPointerDown={handleDown}
        onPointerUp={handleUp}
        onPointerLeave={() => setHoverNode(null)}
        onWheel={handleWheel}
      />

      {hoverNode ? <HoverTip node={hoverNode} pos={hoverPos} /> : null}

      <div className="web-controls">
        <button className="web-control-btn" onClick={() => zoomBy(1.2)} title="Zoom in">
          <Plus size={14} />
        </button>
        <button className="web-control-btn" onClick={() => zoomBy(1 / 1.2)} title="Zoom out">
          <Minus size={14} />
        </button>
        <button className="web-control-btn" onClick={recenter} title="Recenter">
          <Maximize2 size={14} />
        </button>
        <button className="web-control-btn" onClick={reheat} title="Reheat layout">
          <RotateCcw size={14} />
        </button>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------- */
/*  Physics                                                             */
/* -------------------------------------------------------------------- */

function step(s, nodes, edges) {
  s.pulsePhase = (s.pulsePhase + 0.05) % (Math.PI * 2);

  const cx = s.width / 2;
  const cy = s.height / 2;
  const arr = nodes.map((n) => ({ id: n.id, p: s.parts.get(n.id) })).filter((x) => x.p);

  // Centering force (very gentle)
  arr.forEach(({ p }) => {
    p.vx += (cx - p.x) * 0.0008;
    p.vy += (cy - p.y) * 0.0008;
  });

  // Repulsion (Coulomb-like, O(n^2) — fine for ~80 nodes)
  for (let i = 0; i < arr.length; i++) {
    const a = arr[i].p;
    for (let j = i + 1; j < arr.length; j++) {
      const b = arr[j].p;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distSq = dx * dx + dy * dy + 0.01;
      const dist = Math.sqrt(distSq);
      const minDist = a.r + b.r + 24;
      // strong short-range repel, weak long-range
      const force = 4500 / distSq;
      // collision push
      const overlap = Math.max(0, minDist - dist);
      const f = (force + overlap * 0.5) / dist;
      const fx = dx * f;
      const fy = dy * f;
      a.vx -= fx / a.mass;
      a.vy -= fy / a.mass;
      b.vx += fx / b.mass;
      b.vy += fy / b.mass;
    }
  }

  // Spring force on edges
  edges.forEach((e) => {
    const a = s.parts.get(e.source);
    const b = s.parts.get(e.target);
    if (!a || !b) return;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.sqrt(dx * dx + dy * dy) + 0.01;
    const rest =
      e.type === "cross_pollination" ? 220 :
      e.type === "mutation" ? 110 :
      e.type === "parent" ? 130 : 140;
    const k = e.type === "cross_pollination" ? 0.012 : 0.04;
    const f = (dist - rest) * k;
    const fx = (dx / dist) * f;
    const fy = (dy / dist) * f;
    a.vx += fx / a.mass;
    a.vy += fy / a.mass;
    b.vx -= fx / b.mass;
    b.vy -= fy / b.mass;
  });

  // Integrate + damp
  arr.forEach(({ p }) => {
    p.vx *= 0.85;
    p.vy *= 0.85;
    p.x += p.vx;
    p.y += p.vy;
  });
}

/* -------------------------------------------------------------------- */
/*  Drawing                                                             */
/* -------------------------------------------------------------------- */

function nodeRadius(n) {
  if (n.type === "hook") return 9;
  if (n.type === "concept") {
    const base = 22;
    const spend = Math.log10(Math.max(1, n.spend || 1)) * 4;
    return base + Math.min(20, spend);
  }
  // variation
  const base = 13;
  const spend = Math.log10(Math.max(1, n.spend || 1)) * 2.5;
  return base + Math.min(14, spend);
}

function statusFill(n, opts) {
  if (n.status === "killed") return "#3a3f47";
  if (n.status === "unexplored") return opts.showGaps ? "rgba(34,211,238,0.10)" : "rgba(120,120,120,0.06)";
  if (n.status === "paused") return "rgba(148,163,184,0.55)";
  if (n.scaling || n.status === "scaling") return "#c084fc";
  return "#a78bfa";
}

function statusStroke(n, opts) {
  if (n.type === "hook") return "rgba(212,175,55,0.55)";
  if (n.status === "unexplored") return opts.showGaps ? "rgba(34,211,238,0.65)" : "rgba(120,120,120,0.25)";
  if (n.funnel === "tof") return "#22d3ee";
  if (n.funnel === "mof") return "#f59e0b";
  if (n.funnel === "bof") return "#ec4899";
  return "rgba(255,255,255,0.4)";
}

function haloTier(n) {
  if (n.status === "unexplored" || n.status === "killed") return 0;
  const roas = n.roas || 0;
  if (roas >= 4) return 3; // gold
  if (roas >= 3) return 2; // silver
  if (roas >= 2.5) return 1; // bronze
  return 0;
}

function draw(canvas, s, nodes, edges, opts) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.save();
  ctx.clearRect(0, 0, s.width, s.height);

  // Heat Atlas overlay (large blobs behind everything)
  if (opts.showHeatAtlas) {
    drawHeatAtlas(ctx, s, nodes);
  }

  ctx.translate(s.pan.x, s.pan.y);
  ctx.scale(s.zoom, s.zoom);

  // Edges
  edges.forEach((e) => {
    const a = s.parts.get(e.source);
    const b = s.parts.get(e.target);
    if (!a || !b) return;

    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);

    if (e.type === "cross_pollination") {
      ctx.strokeStyle = "rgba(212,175,55,0.55)";
      ctx.lineWidth = 1.4;
      ctx.setLineDash([2, 4]);
    } else if (e.type === "mutation") {
      ctx.strokeStyle = "rgba(167,139,250,0.55)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
    } else {
      ctx.strokeStyle = "rgba(255,255,255,0.10)";
      ctx.lineWidth = 1.2;
      ctx.setLineDash([]);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  });

  // Nodes
  nodes.forEach((n) => {
    const p = s.parts.get(n.id);
    if (!p) return;

    const dimmed = opts.dimmedIds?.has?.(n.id);
    ctx.save();
    if (dimmed) ctx.globalAlpha = 0.18;

    const isHover = s.hoverId === n.id;
    const isSelected = opts.selectedId === n.id;
    const isHighlight = opts.highlightId === n.id;

    const tier = haloTier(n);
    if (tier > 0) {
      const haloColor =
        tier === 3 ? "rgba(247,208,71,0.55)" :
        tier === 2 ? "rgba(192,200,210,0.5)" :
                     "rgba(176,141,87,0.45)";
      const haloR = p.r + (tier === 3 ? 10 : tier === 2 ? 8 : 6);
      const grad = ctx.createRadialGradient(p.x, p.y, p.r * 0.6, p.x, p.y, haloR);
      grad.addColorStop(0, haloColor);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(p.x, p.y, haloR, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Pulse for scaling
    if (n.scaling || n.status === "scaling") {
      const pulse = (Math.sin(s.pulsePhase) + 1) * 0.5;
      const pr = p.r + 6 + pulse * 6;
      ctx.beginPath();
      ctx.arc(p.x, p.y, pr, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(192,132,252,${0.35 * (1 - pulse)})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Selected ring
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r + 8, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(247,208,71,0.9)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Highlight ring (e.g. linked from a daily card)
    if (isHighlight && !isSelected) {
      const t = (Math.sin(s.pulsePhase * 1.6) + 1) * 0.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r + 12 + t * 4, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(247,208,71,${0.4 + t * 0.4})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Body fill
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);

    if (n.type === "hook") {
      // Diamond-like rounded body, gold tint
      ctx.fillStyle = "rgba(212,175,55,0.18)";
    } else {
      ctx.fillStyle = statusFill(n, opts);
    }
    ctx.fill();

    // Border (funnel-stage color or hook accent)
    const isUnexplored = n.status === "unexplored";
    const conf = n.confidence || 0.5;
    ctx.lineWidth = isUnexplored ? 1 : 1 + conf * 2.5;
    ctx.strokeStyle = statusStroke(n, opts);
    if (isUnexplored) ctx.setLineDash([3, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Inner highlight for concepts (gives depth)
    if (n.type === "concept" && n.status !== "killed" && n.status !== "unexplored") {
      const ig = ctx.createRadialGradient(p.x - p.r * 0.3, p.y - p.r * 0.3, 0, p.x, p.y, p.r);
      ig.addColorStop(0, "rgba(255,255,255,0.18)");
      ig.addColorStop(1, "rgba(255,255,255,0)");
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = ig;
      ctx.fill();
    }

    // Label — only for concepts and on hover/selection
    const showLabel = n.type === "concept" || isHover || isSelected;
    if (showLabel) {
      ctx.font = `${n.type === "concept" ? "700" : "500"} ${
        n.type === "concept" ? 12 : 11
      }px Inter, system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const label = n.label || n.id;
      const lblY = p.y + p.r + 14;

      // dark backdrop for legibility
      const w = ctx.measureText(label).width + 12;
      ctx.fillStyle = "rgba(11,13,16,0.78)";
      roundedRect(ctx, p.x - w / 2, lblY - 9, w, 18, 4);
      ctx.fill();

      ctx.fillStyle = isUnexplored
        ? "rgba(255,255,255,0.45)"
        : "rgba(245,241,232,0.95)";
      ctx.fillText(label, p.x, lblY);
    }

    ctx.restore();
  });

  ctx.restore();
}

function drawHeatAtlas(ctx, s, nodes) {
  ctx.save();
  ctx.translate(s.pan.x, s.pan.y);
  ctx.scale(s.zoom, s.zoom);

  nodes.forEach((n) => {
    const p = s.parts.get(n.id);
    if (!p) return;
    if (n.status === "unexplored") return;
    const intensity = Math.min(1, ((n.spend || 0) / 25000));
    const r = 80 + intensity * 80;
    const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
    grad.addColorStop(0, `rgba(167,139,250,${0.18 * intensity})`);
    grad.addColorStop(1, "rgba(167,139,250,0)");
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
  });

  // Untested gap markers
  nodes.filter((n) => n.status === "unexplored").forEach((n) => {
    const p = s.parts.get(n.id);
    if (!p) return;
    const r = 100;
    const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
    grad.addColorStop(0, "rgba(34,211,238,0.18)");
    grad.addColorStop(1, "rgba(34,211,238,0)");
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
  });

  ctx.restore();
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
}

/* -------------------------------------------------------------------- */
/*  HoverTip                                                            */
/* -------------------------------------------------------------------- */

function HoverTip({ node, pos }) {
  // Force-apply colors inline so the tooltip stays readable even if CSS
  // tokens haven't cascaded (e.g. embedded in a host that strips them).
  // Flip horizontally when near the right edge so it never clips off-screen.
  const TOOLTIP_W = 240;
  const flipLeft =
    typeof window !== "undefined" && pos.x > window.innerWidth - TOOLTIP_W - 24;

  const baseStyle = {
    left: pos.x,
    top: pos.y,
    background: "rgba(11,13,16,0.96)",
    color: "#f5f1e8",
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: 10,
    padding: "10px 12px",
    minWidth: 220,
    maxWidth: TOOLTIP_W,
    boxShadow: "0 18px 32px -10px rgba(0,0,0,0.7)",
    fontSize: 12,
    transform: flipLeft ? "translate(calc(-100% - 16px), -50%)" : "translate(12px, -50%)",
    pointerEvents: "none",
    zIndex: 100,
    position: "absolute",
  };

  const labelStyle = { color: "#d4d4d8", fontSize: 11 };
  const valueStyle = { color: "#ffffff", fontFamily: "JetBrains Mono, ui-monospace, monospace", fontSize: 11, fontWeight: 600 };
  const titleStyle = {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    lineHeight: 1.3,
    wordBreak: "break-word",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  };
  const rowStyle = { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "2px 0" };
  const chipStyle = {
    fontSize: 10,
    padding: "2px 7px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.04)",
    color: "#c8c2b6",
  };
  const chipsRow = { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 };

  return (
    <div className="web-tip" style={baseStyle}>
      <div style={titleStyle}>{node.label}</div>
      {node.type === "hook" ? (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Reuses</span>
            <b style={valueStyle}>{node.reuses}</b>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Win rate</span>
            <b style={valueStyle}>{Math.round((node.winRate || 0) * 100)}%</b>
          </div>
          {node.archetype ? (
            <div style={chipsRow}>
              <span style={chipStyle}>{node.archetype}</span>
            </div>
          ) : null}
        </>
      ) : node.status === "unexplored" ? (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Status</span>
            <b style={{ ...valueStyle, color: "#22d3ee" }}>UNEXPLORED</b>
          </div>
          {node.thesis ? (
            <div style={{ marginTop: 6, color: "#c8c2b6", fontSize: 11, lineHeight: 1.5 }}>
              {node.thesis}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Spend</span>
            <b style={valueStyle}>${(node.spend || 0).toLocaleString()}</b>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>ROAS</span>
            <b style={{ ...valueStyle, color: (node.roas || 0) >= 3 ? "#f7d047" : "#f5f1e8" }}>
              {(node.roas || 0).toFixed(2)}
            </b>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>CTR</span>
            <b style={valueStyle}>{(node.ctr || 0).toFixed(2)}%</b>
          </div>
          {showsHookRate(node) ? (
            <div style={rowStyle}>
              <span style={labelStyle}>Hook rate</span>
              <b style={valueStyle}>{Math.round((node.hookRate || 0) * 100)}%</b>
            </div>
          ) : null}
          {(node.funnel || node.awareness || node.angle) ? (
            <div style={chipsRow}>
              {node.funnel ? <span style={chipStyle}>{node.funnel.toUpperCase()}</span> : null}
              {node.awareness ? <span style={chipStyle}>{node.awareness}</span> : null}
              {node.angle ? <span style={chipStyle}>{node.angle}</span> : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// Hook rate is meaningless on static formats — outline §6.4. Keep the predicate
// in one place so AdDetailModal + tooltip behave consistently.
function showsHookRate(node) {
  const f = String(node.format || "").toLowerCase();
  if (!f) return (node.hookRate || 0) > 0;
  if (f.startsWith("video")) return true;
  if (f === "carousel") return true;
  return false;
}

export { showsHookRate };
